import { SupabaseClient, createClient } from '@supabase/supabase-js';
import { create } from 'zustand';

import { SUPABASE_PROVIDERS } from '@/features/auth/constants/supabase-providers';
import { SupabaseProviderConfig, SupabaseProviderId } from '@/features/auth/types/supabase';
import { StorageKeys, StorageService } from '@/services/storage.service';

interface SupabaseProviderServiceState {
  loadedProviderId: SupabaseProviderId | null;
  client: SupabaseClient | null;
  loading: boolean;
  setLoadedProviderId: (loadedProviderId: SupabaseProviderId) => void;
  setClient: (client: SupabaseClient) => void;
  setLoading: (loading: boolean) => void;
}

export const useSupabaseProviderStore = create<SupabaseProviderServiceState>(set => ({
  loadedProviderId: null,
  client: null,
  loading: true,
  setLoadedProviderId: loadedProviderId => set({ loadedProviderId }),
  setLoading: loading => set({ loading }),
  setClient: client => set({ client }),
}));

class SupabaseProviderService {
  constructor(private readonly providers: SupabaseProviderConfig[]) {}

  init() {
    if (this.client) {
      return;
    }

    useSupabaseProviderStore.getState().setLoading(true);
    try {
      const storedProviderId = StorageService.getItem(StorageKeys.USED_SUPABASE_PROVIDER_ID);

      if (storedProviderId) {
        this.loadProviderById(storedProviderId as SupabaseProviderId, true);
      } else {
        this.loadDefaultProvider();
      }
    } finally {
      useSupabaseProviderStore.getState().setLoading(false);
    }
  }

  get client(): SupabaseClient | null {
    return useSupabaseProviderStore.getState().client;
  }

  get isSSO() {
    const providerId = useSupabaseProviderStore.getState().loadedProviderId;

    if (!providerId) {
      return false;
    }

    const provider = this.providers.find(candidate => candidate.id === providerId);

    return Boolean(provider?.ssoClient);
  }

  private createClient(provider: SupabaseProviderConfig) {
    return createClient(provider.supabaseUrl, provider.anonKey, {
      auth: {
        storageKey: `supabase-client-${provider.id}`,
        autoRefreshToken: true,
        persistSession: true,
      },
    });
  }

  hasProviderByDomain(domain: string) {
    const normalizedDomain = domain.toLowerCase();

    return this.providers.some(provider =>
      provider.ssoValidDomains?.some(validDomain => validDomain.toLowerCase() === normalizedDomain)
    );
  }

  loadProviderByDomain(domain: string, fallbackToDefault = false) {
    const normalizedDomain = domain.toLowerCase();
    let provider = this.providers.find(candidate =>
      candidate.ssoValidDomains?.some(validDomain => validDomain.toLowerCase() === normalizedDomain)
    );

    if (!provider) {
      if (!fallbackToDefault) {
        throw new Error(`Provider for domain ${domain} not found`);
      }

      provider = this.providers.find(candidate => candidate.isDefault);
    }

    if (!provider) {
      throw new Error('Default provider not found');
    }

    useSupabaseProviderStore.getState().setLoadedProviderId(provider.id);
    useSupabaseProviderStore.getState().setClient(this.createClient(provider));

    return provider;
  }

  loadProviderById(id: SupabaseProviderId, fallbackToDefault = false) {
    let provider = this.providers.find(candidate => candidate.id === id);

    if (!provider && fallbackToDefault) {
      provider = this.providers.find(candidate => candidate.isDefault);
    }

    if (!provider) {
      this.clearPersistedProvider();
      throw new Error(`Provider with id ${id} not found`);
    }

    useSupabaseProviderStore.getState().setLoadedProviderId(provider.id);
    useSupabaseProviderStore.getState().setClient(this.createClient(provider));

    return provider;
  }

  loadDefaultProvider() {
    const defaultProvider = this.providers.find(provider => provider.isDefault);

    if (!defaultProvider) {
      throw new Error('Default provider not found');
    }

    useSupabaseProviderStore.getState().setLoadedProviderId(defaultProvider.id);
    useSupabaseProviderStore.getState().setClient(this.createClient(defaultProvider));

    return defaultProvider;
  }

  persistLoadedProvider() {
    const loadedProviderId = useSupabaseProviderStore.getState().loadedProviderId;

    if (!loadedProviderId) {
      throw new Error('Provider not loaded');
    }

    StorageService.setItem(StorageKeys.USED_SUPABASE_PROVIDER_ID, loadedProviderId);
  }

  clearPersistedProvider() {
    StorageService.removeItem(StorageKeys.USED_SUPABASE_PROVIDER_ID);
  }
}

const supabaseProviderService = new SupabaseProviderService(SUPABASE_PROVIDERS);

export default supabaseProviderService;
